import React, { useEffect, useState } from 'react';
import { api, getAuctionPeriods, getBidsForUser, getPlacedBidsForUser, getUniqueBidTargetsForUser } from '../api';
import { getSessionsDaily, getBidsDaily } from '../api';
import {
  Paper, Typography, Box, CircularProgress, Grid, Alert,
  FormControl, InputLabel, Select, MenuItem, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Chip
} from '@mui/material';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import dayjs from 'dayjs';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LoginIcon from '@mui/icons-material/Login';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import GavelIcon from '@mui/icons-material/Gavel';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import VerifiedIcon from '@mui/icons-material/Verified';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import TransgenderIcon from '@mui/icons-material/Transgender';
import { useTheme } from '@mui/material/styles';
import PageHeader from './common/PageHeader';
import StatCard from './common/StatCard';

// Helper moved to title/helpText in common StatCard

// Helper function to find the current auction period
const findCurrentPeriod = (periods) => {
  if (!periods || periods.length === 0) return 'all';
  
  const now = new Date();
  
  // First, try to find a period where now is between start_time and end_time
  const activePeriod = periods.find(period => {
    if (!period.start_time || !period.end_time) return false;
    const start = new Date(period.start_time);
    const end = new Date(period.end_time);
    return now >= start && now <= end;
  });
  
  if (activePeriod) return activePeriod.id;
  
  // If no active period, return the most recent period (first in DESC order)
  return periods[0]?.id || 'all';
};

// Helper function to format the label for the dropdown
const formatPeriodLabel = (period) => {
  if (!period.start_time) return `Period ${period.id}`;
  const date = new Date(period.start_time);
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `ID ${period.id} - (${month}/${day}/${year})`;
};

export default function UserActivityReport() {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null); // Tracks which user row is expanded
  const [bidDetails, setBidDetails] = useState([]); // Stores the fetched bid details
  const [isBidsLoading, setIsBidsLoading] = useState(false); // Tracks loading state for bids
  const [expandedBidder, setExpandedBidder] = useState(null); // Tracks expanded bidder row
  const [bidderDetails, setBidderDetails] = useState([]); // Stores fetched details for bidders
  const [isBidderDetailsLoading, setIsBidderDetailsLoading] = useState(false); // Loading state for bidder details

  // Daily claims state
  const [dailyClaims, setDailyClaims] = useState(null);
  const [claimsLeaderboard, setClaimsLeaderboard] = useState([]);
  const [claimsKpis, setClaimsKpis] = useState(null);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [claimsError, setClaimsError] = useState(null);
  const [showParticipationRate, setShowParticipationRate] = useState(true);
  const [showRolling7d, setShowRolling7d] = useState(true);
  // New daily series state
  const [sessionsDaily, setSessionsDaily] = useState([]);
  const [bidsDaily, setBidsDaily] = useState([]);

  // Effect to fetch the list of auction periods for the dropdown
  useEffect(() => {
    getAuctionPeriods()
      .then(data => {
        setPeriods(data);
        // Set the default period to current period on initial load
        const currentPeriod = findCurrentPeriod(data);
        setSelectedPeriod(currentPeriod);
      })
      .catch(err => {
        console.error("Failed to load auction periods", err);
        // If fetching periods fails, default to 'all' so the report still loads
        setSelectedPeriod('all');
      });
  }, []);

  // Effect to fetch the main activity data + new daily series, depends on selectedPeriod
  useEffect(() => {
    // Don't fetch until we have a selected period
    if (selectedPeriod === null) return;
    
    setLoading(true);
    Promise.all([
      api.get('/activity-report', { params: { periodId: selectedPeriod } }),
      getSessionsDaily({ periodId: selectedPeriod }),
      getBidsDaily({ periodId: selectedPeriod })
    ])
      .then(([res, sess, bids]) => {
        const { dailyClaims, claimsLeaderboard, claimsKpis, ...mainData } = res.data;
        setData(mainData);
        setDailyClaims(dailyClaims || []);
        setClaimsLeaderboard(claimsLeaderboard || []);
        setClaimsKpis(claimsKpis || null);

        setSessionsDaily(sess?.data || []);
        setBidsDaily(bids?.data || []);
        setError(null);
        setClaimsError(null);
      })
      .catch(err => {
        console.error('Error fetching user activity data:', err);
        const errorMessage = err.response?.data?.error || 'Failed to load activity data. Please ensure all required database tables exist.';
        setError(errorMessage);
      })
      .finally(() => setLoading(false));
  }, [selectedPeriod]);
  // Remove separate claims loader; claims data is included in activity-report now

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  const handleTotalBidsClick = async (userId) => {
    // If the user clicks the same row that is already expanded, collapse it.
    if (expandedRow === userId) {
      setExpandedRow(null);
      setBidDetails([]);
      return;
    }

    // Otherwise, fetch data for the new row.
    setIsBidsLoading(true);
    setExpandedRow(userId); // Immediately show the loading state under the correct row
    try {
      const bids = await getBidsForUser(userId, selectedPeriod);
      setBidDetails(bids);
    } catch (error) {
      // You could set an error state here to show a message in the expanded row
      console.error("Failed to load bid details", error);
      setBidDetails([]); // Clear previous details on error
    } finally {
      setIsBidsLoading(false);
    }
  };

  const handleBidderRowClick = async (userId, type, topBidAmount) => {
    // Collapse if the same cell is clicked again
    if (expandedBidder?.userId === userId && expandedBidder?.type === type) {
      setExpandedBidder(null);
      setBidderDetails([]);
      return;
    }

    setIsBidderDetailsLoading(true);
    setExpandedBidder({ userId, type }); // Expand the row and show loading spinner

    try {
      if (type === 'totalBids' || type === 'topBid') {
        const bids = await getPlacedBidsForUser(userId, selectedPeriod);
        if (type === 'topBid') {
          // Find the single top bid to display
          const topBid = bids.find(b => b.bidAmount === topBidAmount);
          setBidderDetails(topBid ? [topBid] : []);
        } else {
          setBidderDetails(bids);
        }
      } else if (type === 'uniqueProfiles') {
        const targets = await getUniqueBidTargetsForUser(userId, selectedPeriod);
        setBidderDetails(targets);
      }
    } catch (error) {
      console.error("Failed to load bidder details", error);
      setBidderDetails([]); // Clear details on error
    } finally {
      setIsBidderDetailsLoading(false);
    }
  };

  if (loading && !data) { // Show initial loading spinner
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        <Typography variant="h6">Error Loading Report</Typography>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  // Gender helpers
  const genderKey = (g = '') => {
    const v = String(g).toLowerCase();
    if (v.startsWith('m')) return 'male';
    if (v.startsWith('f')) return 'female';
    if (v.includes('non')) return 'nonbinary';
    return 'other';
  };
  const genderMeta = {
    male:      { label: 'Male', color: '#2196f3', icon: <MaleIcon fontSize="small" /> },
    female:    { label: 'Female', color: '#e91e63', icon: <FemaleIcon fontSize="small" /> },
    nonbinary: { label: 'Non-binary', color: '#9c27b0', icon: <TransgenderIcon fontSize="small" /> },
    other:     { label: 'Other', color: '#607d8b', icon: <TransgenderIcon fontSize="small" /> },
  };
  const toBreakdown = (rows = []) => {
    const order = ['male', 'female', 'nonbinary', 'other'];
    const agg = rows.reduce((acc, r) => {
      const key = genderKey(r.gender);
      const val = r.count ?? r.cnt ?? 0;
      acc[key] = (acc[key] || 0) + val;
      return acc;
    }, {});
    return order
      .filter(k => agg[k] > 0)
      .map(k => ({ label: genderMeta[k].label, value: agg[k], icon: genderMeta[k].icon, color: genderMeta[k].color }));
  };

  // Build Daily Claims chart data
  const labels = dailyClaims?.map(d => dayjs(d.date).format('MMM D')) || [];
  const datasets = [];
  // Brand colors
  const PURPLE = '#702ad0';
  const PURPLE_LIGHT = 'rgba(112,42,208,0.15)';
  const SLATE = '#6b7280';
  const SLATE_LIGHT = 'rgba(107,114,128,0.15)';
  const TEAL = '#10b981';
  const VIOLET_SOFT = '#a78bfa';
  if (dailyClaims) {
    datasets.push({
      label: 'Total Claims',
      data: dailyClaims.map(d => d.claims),
      borderColor: PURPLE,
      backgroundColor: PURPLE_LIGHT,
      borderWidth: 2,
      pointRadius: 3,
      tension: 0.25,
      fill: false
    });
    datasets.push({
      label: 'Unique Claimers',
      data: dailyClaims.map(d => d.uniqueClaimers),
      borderColor: SLATE,
      backgroundColor: SLATE_LIGHT,
      borderWidth: 2,
      pointRadius: 3,
      tension: 0.25,
      fill: false
    });
    if (showParticipationRate) {
      datasets.push({
        label: 'Participation %',
        data: dailyClaims.map(d => Number((d.participationRate * 100).toFixed(2))),
        yAxisID: 'y1',
        borderColor: VIOLET_SOFT,
        borderDash: [6,4],
        pointRadius: 3,
        tension: 0.25,
        fill: false,
        datalabels: { display: false }
      });
    }
    if (showRolling7d) {
      datasets.push({
        label: 'Rolling 7-day Avg (unique claimers)',
        data: dailyClaims.map(d => d.rolling7dAvgUniqueClaimers ?? d.rolling7dUniqueClaimers),
        borderColor: TEAL,
        borderDash: [6, 6],
        pointRadius: 0,
        tension: 0.25,
        fill: false,
        datalabels: { display: false }
      });
    }
  }
  // Add headroom to prevent label overlap with header chips
  const maxY = dailyClaims ? Math.max(
    ...dailyClaims.map(r => Math.max(r.claims, r.uniqueClaimers, r.rolling7dAvgUniqueClaimers || 0))
  ) : 0;
  const suggestedMax = Math.ceil(maxY * 1.15);

  const claimsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 28, right: 16, bottom: 8, left: 8 } },
    plugins: {
      legend: { position: 'top' },
      datalabels: {
        clip: true,
        clamp: true,
        offset: 6,
        formatter: (value, ctx) => {
          const { chart, datasetIndex, dataIndex } = ctx;
          const meta = chart.getDatasetMeta(datasetIndex);
          const pt = meta?.data?.[dataIndex];
          if (pt && pt.y <= chart.chartArea.top + 12) return '';
          return value;
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax,
        ticks: { precision: 0 }
      },
      y1: {
        min: 0, max: 100,
        position: 'right',
        grid: { drawOnChartArea: false },
        title: { display: true, text: '% Participation' },
        ticks: { callback: (v) => `${v}%` }
      }
    }
  };
  const claimsLineData = dailyClaims ? { labels, datasets } : null;

  // --- Daily Sessions chart data ---
  const sessionsLabels = sessionsDaily.map(d => dayjs(d.date).format('MMM D'));
  const sessionsData = sessionsDaily.map(d => d.sessions);
  const sessionsUnique = sessionsDaily.map(d => d.uniqueUsers);

  const sessionsLineData = sessionsDaily.length ? {
    labels: sessionsLabels,
    datasets: [
      {
        label: 'Unique users with session',
        data: sessionsUnique,
        borderColor: '#10b981',
        pointRadius: 0,
        tension: 0.25,
        fill: false,
        borderWidth: 2,
      },
      {
        label: 'Total sessions',
        data: sessionsData,
        borderColor: theme.palette.primary.main,
        backgroundColor: 'rgba(149,62,241,0.15)',
        pointRadius: 0,
        tension: 0.25,
        fill: true,
        borderWidth: 2,
      }
    ]
  } : null;

  // --- Bids by Day chart data ---
  const bidsLabels = bidsDaily.map(d => dayjs(d.date).format('MMM D'));
  const bidsTotal  = bidsDaily.map(d => d.totalBids);
  const bidsUniqueBidders    = bidsDaily.map(d => d.uniqueBidders);
  const bidsUniqueRecipients = bidsDaily.map(d => d.uniqueRecipients);

  const bidsLineData = bidsDaily.length ? {
    labels: bidsLabels,
    datasets: [
      {
        label: 'Unique bidders',
        data: bidsUniqueBidders,
        borderColor: '#3b82f6',
        pointRadius: 0,
        tension: 0.25,
        fill: false,
        borderWidth: 2,
      },
      {
        label: 'Unique recipients',
        data: bidsUniqueRecipients,
        borderColor: '#f59e0b',
        pointRadius: 0,
        tension: 0.25,
        fill: false,
        borderWidth: 2,
      },
      {
        label: 'Total bids placed',
        data: bidsTotal,
        borderColor: '#702ad0',
        backgroundColor: 'rgba(112,42,208,0.12)',
        pointRadius: 0,
        tension: 0.25,
        fill: true,
        borderWidth: 2,
      }
    ]
  } : null;

  // consistent options
  const smallLineOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' }, datalabels: { display: false } },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } }
    }
  };

  return (
    <Box>
      <PageHeader
        title="User Activity Report"
        actions={(
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel id="auction-period-select-label">Auction Period</InputLabel>
            <Select labelId="auction-period-select-label" id="auction-period-select" value={selectedPeriod || ''} label="Auction Period" onChange={handlePeriodChange}>
              <MenuItem value="all"><em>All Time</em></MenuItem>
              {periods.map(period => (
                <MenuItem key={period.id} value={period.id}>{formatPeriodLabel(period)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
        “Profiles approved this period” assumes approval occurs at completion time. Since approval timestamps aren’t stored, minor discrepancies are possible.
      </Alert>

      {loading && ( // Show a subtle loading indicator over the content
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 10 }}>
            <CircularProgress />
        </Box>
      )}

      {/* Main content wrapper using block layout for better spacing control */}
      <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.3s' }}>

        {/* SECTION 1: Stat Cards Grid */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}><StatCard title="Completed Profiles" value={(data.completedProfiles || []).reduce((s,i)=>s+i.count,0)} breakdown={toBreakdown(data.completedProfiles)} icon={<PeopleIcon />} /></Grid>
          <Grid item xs={12} sm={6} md={4}><StatCard title="Approved Profiles" value={(data.approvedProfiles || []).reduce((s,i)=>s+i.count,0)} breakdown={toBreakdown(data.approvedProfiles)} icon={<CheckCircleOutlineIcon />} /></Grid>
          {/* NEW: Profiles created this period */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Profiles created this period"
              value={(data.createdThisPeriod || []).reduce((s,i)=>s+i.count,0)}
              breakdown={toBreakdown(data.createdThisPeriod)}
              icon={<PersonAddAltIcon />}
            />
          </Grid>
          {/* NEW: Profiles approved this period */}
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Profiles approved this period"
              value={(data.approvedThisPeriod || []).reduce((s,i)=>s+i.count,0)}
              breakdown={toBreakdown(data.approvedThisPeriod)}
              icon={<VerifiedIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}><StatCard title="Total Logged-in Sessions" value={(data.totalLogins || []).reduce((s,i)=>s+i.count,0)} breakdown={toBreakdown(data.totalLogins)} icon={<LoginIcon />} /></Grid>
          <Grid item xs={12} sm={6} md={4}><StatCard title="Unique Users with Sessions" value={(data.uniqueUserLogins || []).reduce((s,i)=>s+i.count,0)} breakdown={toBreakdown(data.uniqueUserLogins)} icon={<HowToRegIcon />} /></Grid>
          <Grid item xs={12} sm={6} md={4}><StatCard title="Users Who Placed a Bid" value={(data.usersPlacedBid || []).reduce((s,i)=>s+i.count,0)} breakdown={toBreakdown(data.usersPlacedBid)} icon={<GavelIcon />} /></Grid>
          <Grid item xs={12} sm={6} md={4}><StatCard title="Users Who Received a Bid" value={(data.usersReceivedBid || []).reduce((s,i)=>s+i.count,0)} breakdown={toBreakdown(data.usersReceivedBid)} icon={<MonetizationOnIcon />} /></Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Avg Participation Rate"
              value={claimsKpis ? `${Math.round(claimsKpis.avgParticipationRate * 100)}%` : '—'}
              icon={<AssessmentIcon />}
              color="secondary"
              helpText="Unique claimers ÷ unique active (avg of daily rates)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Avg Claims per Claimer"
              value={claimsKpis ? claimsKpis.avgClaimsPerClaimer.toFixed(2) : '—'}
              icon={<MonetizationOnIcon />}
              helpText="Total claims ÷ unique claimers in period"
            />
          </Grid>
        </Grid>

        {/* SECTION 2: Daily Claims Chart + Leaderboard */}
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
          <Grid item xs={12} md={8}>
            <Paper sx={{ p:2 }}>
              <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:1 }}>
                <Typography variant="h6">Daily Claims</Typography>
                <Box sx={{ display:'flex', gap:1 }}>
                  <Chip
                    size="small"
                    label="Participation %"
                    color={showParticipationRate ? 'primary' : 'default'}
                    onClick={() => setShowParticipationRate(v => !v)}
                    variant={showParticipationRate ? 'filled' : 'outlined'}
                  />
                  <Chip
                    size="small"
                    label="Rolling 7-day"
                    color={showRolling7d ? 'primary' : 'default'}
                    onClick={() => setShowRolling7d(v => !v)}
                    variant={showRolling7d ? 'filled' : 'outlined'}
                  />
                </Box>
              </Box>
              {loadingClaims ? (
                <Box sx={{ display:'flex', justifyContent:'center', height:300, alignItems:'center' }}>
                  <CircularProgress />
                </Box>
              ) : claimsError ? (
                <Alert severity="error">{claimsError}</Alert>
              ) : claimsLineData ? (
                <Box sx={{ height: 360 }}>
                  <Line data={claimsLineData} options={claimsOptions} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p:3 }}>No claim data for the selected period.</Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p:2 }}>
              <Typography variant="h6" sx={{ mb:1 }}>Claims Leaderboard</Typography>
              {loadingClaims ? (
                <Box sx={{ display:'flex', justifyContent:'center', height:200, alignItems:'center' }}>
                  <CircularProgress />
                </Box>
              ) : claimsError ? (
                <Alert severity="error">{claimsError}</Alert>
              ) : claimsLeaderboard.length ? (
                <TableContainer sx={{ maxHeight: 360, overflowY: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell align="right">Claims</TableCell>
                        <TableCell>Last Claim</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {claimsLeaderboard.map((r, i) => (
                        <TableRow key={r.id}>
                          <TableCell>{i+1}</TableCell>
                          <TableCell>
                            <Link
                              to={`/user/${r.id}`}
                              state={{ from: { label: 'User Activity', path: '/user-activity' } }}
                            >
                              {r.username || '(no username)'}
                            </Link>
                          </TableCell>
                          <TableCell align="right">{Number(r.totalClaims).toLocaleString()}</TableCell>
                          <TableCell>{r.lastClaimDate ? dayjs(r.lastClaimDate).format('MMM D, YYYY') : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p:2 }}>No claims found.</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* SECTION 3: First Table */}
        <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Top Users Receiving Bids
                </Typography>
                {data?.usersWithBids?.length > 0 ? (
                    <TableContainer>
                    <Table size="small">
                        <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Gender</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Gets SMS</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Winning Bid</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Total Bids</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Unique Bidders</TableCell>
                        </TableRow>
                        </TableHead>
                        <TableBody>
                        {data.usersWithBids.map((user) => {
                            const canReceiveSms = !(Number(user.twilio_stop) === 1 || Number(user.sms_notification) === 0);
                            return (
                              <React.Fragment key={user.userId}>
                                <TableRow>
                                  <TableCell>
                                    <Link
                                      to={`/user/${user.userId}`}
                                      state={{ from: { label: 'User Activity', path: '/user-activity' } }}
                                      style={{ textDecoration: 'none', color: theme.palette.primary.main, fontWeight: 'bold' }}
                                    >
                                      {user.username || 'N/A'}
                                    </Link>
                                  </TableCell>
                                  <TableCell sx={{ textTransform: 'capitalize' }}>{user.gender || 'N/A'}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={canReceiveSms ? "Yes" : "No"}
                                      color={canReceiveSms ? "success" : "default"}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {user.winningBid ? user.winningBid.toLocaleString() : 'N/A'}
                                  </TableCell>
                                  <TableCell 
                                    align="right" 
                                    onClick={() => handleTotalBidsClick(user.userId)}
                                    sx={{ 
                                      cursor: 'pointer', 
                                      '&:hover': {
                                        backgroundColor: 'action.hover'
                                      }
                                    }}
                                  >
                                    {user.totalBids}
                                  </TableCell>
                                  <TableCell align="right">{user.uniqueBidders}</TableCell>
                                </TableRow>

                                {/* Expanded row for bid details */}
                                {expandedRow === user.userId && (
                                  <TableRow>
                                    <TableCell colSpan={6} sx={{ p: 0 }}>
                                      <Box sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                                        {isBidsLoading ? (
                                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                            <CircularProgress size={24} />
                                          </Box>
                                        ) : bidDetails.length > 0 ? (
                                          <>
                                            <Typography variant="h6" gutterBottom sx={{ pl: 2 }}>
                                              Bidders for {user.username}
                                            </Typography>
                                            <Table size="small" aria-label="bid details">
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell sx={{ fontWeight: 'bold' }}>Bidder</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold' }}>Gender</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Bid Amount</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold' }}>Date & Time</TableCell>
                                                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Period ID</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {bidDetails.map((bid, index) => (
                                                  <TableRow key={index}>
                                                    <TableCell>
                                                      <Link
                                                        to={`/user/${bid.bidderId}`}
                                                        state={{ from: { label: 'User Activity', path: '/user-activity' } }}
                                                        style={{ textDecoration: 'none', color: theme.palette.primary.main }}
                                                      >
                                                        {bid.bidderUsername || 'N/A'}
                                                      </Link>
                                                    </TableCell>
                                                    <TableCell>{bid.bidderGender || 'N/A'}</TableCell>
                                                    <TableCell align="right">{bid.bidAmount.toLocaleString()}</TableCell>
                                                    <TableCell>{new Date(bid.bidTime).toLocaleString()}</TableCell>
                                                    <TableCell align="right">{bid.periodId || 'N/A'}</TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </>
                                        ) : (
                                          <Typography sx={{ p: 2, textAlign: 'center' }} color="text.secondary">
                                            No bid details to display for this period.
                                          </Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                        })}
                        </TableBody>
                    </Table>
                    </TableContainer>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                      No users received bids in the selected period.
                    </Typography>
                )}
        </Paper>

        {/* SECTION: Daily Sessions and Bids by Day */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p:2 }}>
              <Typography variant="h6">Daily Sessions (Unique vs Total)</Typography>
              {sessionsLineData ? (
                <Box sx={{ height: 320 }}>
                  <Line data={sessionsLineData} options={smallLineOpts} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>No session data for the selected period.</Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p:2 }}>
              <Typography variant="h6">Bids by Day</Typography>
              {bidsLineData ? (
                <Box sx={{ height: 320 }}>
                  <Line data={bidsLineData} options={smallLineOpts} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>No bid data for the selected period.</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* SECTION 4: Second Table */}
        <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Top Bidders
                </Typography>
                {data?.topBidders?.length > 0 ? (
                    <TableContainer>
                    <Table size="small">
                        <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Gender</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Total Bids</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Top Bid</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Unique Profiles Bid On</TableCell>
                        </TableRow>
                        </TableHead>
                        <TableBody>
                        {data.topBidders.map((bidder) => (
                            <React.Fragment key={`bidder-${bidder.userId}`}>
                            <TableRow>
                                <TableCell>
                                <Link
                                  to={`/user/${bidder.userId}`}
                                  state={{ from: { label: 'User Activity', path: '/user-activity' } }}
                                  style={{ textDecoration: 'none', color: theme.palette.primary.main, fontWeight: 'bold' }}
                                >
                                    {bidder.username || 'N/A'}
                                </Link>
                                </TableCell>
                                <TableCell sx={{ textTransform: 'capitalize' }}>{bidder.gender || 'N/A'}</TableCell>
                                <TableCell
                                  align="right"
                                  onClick={() => handleBidderRowClick(bidder.userId, 'totalBids')}
                                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                >
                                  {bidder.totalBids}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  onClick={() => handleBidderRowClick(bidder.userId, 'topBid', bidder.topBid)}
                                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                >
                                  {bidder.topBid ? bidder.topBid.toLocaleString() : 'N/A'}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  onClick={() => handleBidderRowClick(bidder.userId, 'uniqueProfiles')}
                                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                >
                                  {bidder.uniqueProfilesBidOn}
                                </TableCell>
                            </TableRow>

                            {/* Expanded row for bidder details */}
                            {expandedBidder?.userId === bidder.userId && (
                                <TableRow>
                                <TableCell colSpan={5} sx={{ p: 0 }}>
                                    <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderLeft: '3px solid', borderColor: 'primary.light' }}>
                                    {isBidderDetailsLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress size={24} />
                                        </Box>
                                    ) : bidderDetails.length > 0 ? (
                                        <>
                                          <Typography variant="h6" gutterBottom sx={{ pl: 2 }}>
                                            {expandedBidder.type === 'totalBids' && `All Bids by ${bidder.username}`}
                                            {expandedBidder.type === 'topBid' && `Top Bid by ${bidder.username}`}
                                            {expandedBidder.type === 'uniqueProfiles' && `Profiles Bid on by ${bidder.username}`}
                                          </Typography>
                                          {expandedBidder.type !== 'uniqueProfiles' ? (
                                            <Table size="small" aria-label="bidder details">
                                                <TableHead>
                                                  <TableRow>
                                                      <TableCell sx={{fontWeight: 'bold'}}>Bid On</TableCell>
                                                      <TableCell sx={{fontWeight: 'bold'}}>Gender</TableCell>
                                                      <TableCell sx={{fontWeight: 'bold'}} align="right">Bid Amount</TableCell>
                                                      <TableCell sx={{fontWeight: 'bold'}}>Date & Time</TableCell>
                                                      <TableCell sx={{fontWeight: 'bold'}} align="right">Period ID</TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {bidderDetails.map((detail, index) => (
                                                      <TableRow key={index}>
                                                        <TableCell>
                                                          <Link
                                                            to={`/user/${detail.targetUserId}`}
                                                            state={{ from: { label: 'User Activity', path: '/user-activity' } }}
                                                            style={{ textDecoration: 'none', color: theme.palette.primary.main }}
                                                          >
                                                            {detail.targetUsername || 'N/A'}
                                                          </Link>
                                                        </TableCell>
                                                        <TableCell>{detail.targetGender || 'N/A'}</TableCell>
                                                        <TableCell align="right">{detail.bidAmount.toLocaleString()}</TableCell>
                                                        <TableCell>{new Date(detail.bidTime).toLocaleString()}</TableCell>
                                                        <TableCell align="right">{detail.periodId || 'N/A'}</TableCell>
                                                      </TableRow>
                                                  ))}
                                                </TableBody>
                                            </Table>
                                          ) : (
                                            <Table size="small" aria-label="unique profiles">
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell sx={{fontWeight: 'bold'}}>Username</TableCell>
                                                  <TableCell sx={{fontWeight: 'bold'}}>Gender</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {bidderDetails.map((detail, index) => (
                                                  <TableRow key={index}>
                                                    <TableCell>
                                                      <Link
                                                        to={`/user/${detail.targetUserId}`}
                                                        state={{ from: { label: 'User Activity', path: '/user-activity' } }}
                                                        style={{ textDecoration: 'none', color: theme.palette.primary.main }}
                                                      >
                                                        {detail.targetUsername || 'N/A'}
                                                      </Link>
                                                    </TableCell>
                                                    <TableCell>{detail.targetGender || 'N/A'}</TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          )}
                                        </>
                                    ) : (
                                        <Typography sx={{ p: 2, textAlign: 'center' }} color="text.secondary">
                                          No details to display for this selection.
                                        </Typography>
                                    )}
                                    </Box>
                                </TableCell>
                                </TableRow>
                            )}
                            </React.Fragment>
                        ))}
                        </TableBody>
                    </Table>
                    </TableContainer>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                        No users placed bids in the selected period.
                    </Typography>
                )}
        </Paper>

      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
        Note: All metrics exclude test accounts. Session and bid metrics are filtered by the selected auction period. Gender breakdown is shown for users where gender is specified.
      </Typography>
    </Box>
  );
}